import axios from 'axios';
import { CarClass } from '../interfaces/Car';

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface EventoLocalCar {
  id: string;
  plate: string;
  model: string;
  carClass: string;
  carClassLabel: string;
  entryDate: string;
  status: string | null;
  statusLabel: string | null;
  /**
   * at-96: data de entrega — só vem preenchida quando o atendimento está em ENTREGA.
   * É o que permite ao cadastro de pós-evento oferecer os veículos já entregues do evento
   * como origem do retorno de garantia.
   */
  entregueEm?: string | null;
  /** at-96: dias decorridos desde a entrega. `null` quando não há data registrada. */
  diasDesdeEntrega?: number | null;
}

export interface EventoLocal {
  id: string;
  eventNumber: number;
  protocol: string | null;
  createdAt: string;
  temAssociado: boolean;
  cars: EventoLocalCar[];
}

/**
 * Veículo envolvido no evento (associado ou terceiro), para pré-preencher o
 * cadastro. A at-54 unificou os caminhos de cadastro com evento: a lista traz
 * todos os veículos do protocolo e a recepção escolhe qual está entrando.
 */
export interface EventoVeiculo {
  id: string;
  plate: string;
  chassis?: string;
  model?: string;
  color?: string;
  marca?: string;
  anoFabricacao?: number;
  modelYear?: number;
  renavam?: string;
  numeroBo?: string;
  protocol?: string;
  vehicleType: 'car' | 'motorcycle';
  /** Classe do veículo no evento — vira o `carClass` do cadastro. */
  tipo: CarClass;
  ownerName?: string;
  /** Só o associado tem telefone na Hinova (o bloco `terceiro` não traz). */
  ownerPhone?: string;
  situacaoEvento?: string;
  /** Valor FIPE que o SGA informou (só o valor, nunca o código). Terceiro costuma vir sem. */
  valorFipe?: number;
}

/** Como a consulta à Hinova terminou, do ponto de vista de quem cadastra. */
export type HinovaStatus = 'ok' | 'nao-encontrado' | 'indisponivel';

export interface EventoLookupResult {
  /** Evento já cadastrado no sistema, com os veículos vinculados. `null` = primeiro veículo do evento. */
  local: EventoLocal | null;
  hinovaStatus: HinovaStatus;
  /** `null` quando não foi possível consultar a Hinova; `[]` quando o protocolo não tem veículo. */
  veiculos: EventoVeiculo[] | null;
}

/**
 * Lookup único do cadastro com evento (at-54): evento local (se já existir) +
 * todos os veículos do protocolo na Hinova, para a recepção escolher qual está
 * entrando na oficina.
 *
 * Substitui o par `fetchEventoLookup`/`fetchEventoLookupLocal` da at-51 — o
 * caminho antigo exigia o evento na base local, o que travava o cadastro de um
 * terceiro que chega antes do associado.
 */
export const fetchEventoLookup = async (numero: string): Promise<EventoLookupResult> => {
  const { data } = await axios.get(
    `${process.env.REACT_APP_BACKEND_URL}/eventos/lookup/${numero}`,
    { headers: authHeader() },
  );
  return data;
};
